// @section 1 Acquisitions / Call Queue — business-broker call targets only.
// Patrick's daily call list: one numbered card per open broker call (firm → rep
// → tap-to-call/email → listing → key points → goal), urgency-sorted, with a
// reusable voicemail-script panel up top.
//
// Live data: GET /api/hq/acquisitions/call-queue?owner= (acquisitions.v_call_queue
// over sandbox_poc). Falls back to the static fixture when the API isn't
// reachable/deployed (fetchCallQueue handles this), so the screen always renders.

import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  fetchCallQueue, fmtDate, fmtUsd,
  type AcqCallRow,
} from '../../lib/acquisitions';
import { useIsMobile } from '../../lib/useIsMobile';

// @subsection 1.1 Phone formatting — display as-is, tel: link with digits-only href.
function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D+/g, '');
  return digits ? `tel:+1${digits}` : null;
}

const URGENCY_ORDER: Record<string, number> = {
  overdue: 0, today: 1, soon: 2, later: 3, undated: 4,
};
const URGENCY_LABEL: Record<string, string> = {
  overdue: 'Overdue', today: 'Today', soon: 'Soon', later: 'Later', undated: 'Undated',
};

const OWNERS = ['patrick', 'adam'];

export default function CallQueue() {
  const [owner, setOwner] = useState<string>('patrick');
  const [rows, setRows] = useState<AcqCallRow[] | null>(null);
  const [source, setSource] = useState<'api' | 'fixture' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [vmOpen, setVmOpen] = useState(false);
  // @subsection 1.2 Phone: tighten the 26px gutters to 14px so cards aren't squeezed.
  const isMobile = useIsMobile();
  const px = isMobile ? 14 : 26;

  // @subsection 1.3 Load (and reload) the queue whenever the owner changes.
  useEffect(() => {
    let live = true;
    setRows(null); setErr(null);
    fetchCallQueue(owner)
      .then((r) => { if (live) { setRows(r.rows); setSource(r.source); } })
      .catch((e) => { if (live) setErr(String(e)); });
    return () => { live = false; };
  }, [owner]);

  // @subsection 1.4 Sort: urgency bucket, then broker firm, then due date.
  const sorted = useMemo(() => {
    const list = [...(rows ?? [])];
    list.sort((a, b) => {
      const ua = URGENCY_ORDER[a.urgency ?? 'undated'] ?? 9;
      const ub = URGENCY_ORDER[b.urgency ?? 'undated'] ?? 9;
      if (ua !== ub) return ua - ub;
      const ba = a.broker_company ?? '', bb = b.broker_company ?? '';
      if (ba !== bb) return ba.localeCompare(bb);
      const da = a.next_action_due ? new Date(a.next_action_due).getTime() : Infinity;
      const db = b.next_action_due ? new Date(b.next_action_due).getTime() : Infinity;
      return da - db;
    });
    return list;
  }, [rows]);

  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', height: '100%', minHeight: 0 }}>
      {/* @subsection 1.4 Filter strip */}
      <div style={{ padding: `10px ${px}px`, borderBottom: '1px solid var(--t2-border)', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--tx-3)', fontSize: 12 }}>
          {rows == null ? 'Loading…' : `${sorted.length} broker ${sorted.length === 1 ? 'call' : 'calls'}`}
        </span>
        <div className="pill-group">
          {OWNERS.map((o) => (
            <button key={o} className={`pill${owner === o ? ' active' : ''}`} onClick={() => setOwner(o)}>
              {o[0].toUpperCase() + o.slice(1)}
            </button>
          ))}
        </div>
        {source === 'fixture' && (
          <span style={{ color: 'var(--warn)', fontSize: 11 }}>· offline sample (live data unavailable)</span>
        )}
      </div>

      {/* @subsection 1.5 Voicemail script panel */}
      <div style={{ padding: `10px ${px}px`, borderBottom: '1px solid var(--t2-border)' }}>
        <button
          onClick={() => setVmOpen((v) => !v)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-3)', fontSize: 12, fontWeight: 600 }}
        >
          {vmOpen ? '▼' : '▶'} Voicemail script
        </button>
        {vmOpen && (
          <div style={{ marginTop: 8, padding: '10px 12px', border: '1px solid var(--t2-border)', borderRadius: 4, fontSize: 13, lineHeight: 1.5, color: 'var(--tx-2)', whiteSpace: 'pre-wrap' }}>
{`Hi [rep name], this is ${owner[0].toUpperCase() + owner.slice(1)} calling from Vertis Holdings about
[listing code / business name]. We've signed your NDA and reviewed the
profile; I have a few questions before we move forward. Please give me a
call back when you have a few minutes. Thanks.`}
          </div>
        )}
      </div>

      {/* @subsection 1.6 Call cards */}
      <div style={{ overflow: 'auto', padding: `14px ${px}px 24px` }}>
        {err && <div style={{ color: 'var(--neg, #f87171)', fontSize: 13, padding: 12 }}>{err}</div>}
        {rows == null && !err && (
          <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 24 }}>Loading call queue…</div>
        )}
        {rows != null && sorted.length === 0 && !err && (
          <div style={{ color: 'var(--tx-3)', fontStyle: 'italic', padding: 24 }}>
            No broker calls in queue for {owner[0].toUpperCase() + owner.slice(1)}.
          </div>
        )}
        {sorted.length > 0 && (
          <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 14 }}>
            {sorted.map((r, i) => (
              <CallCard key={r.activity_id ?? `${r.deal_id}-${i}`} idx={i + 1} row={r} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// @section 2 Call card — broker firm, rep contact, listing, key summary points.
function CallCard({ idx, row }: { idx: number; row: AcqCallRow }) {
  const phone = row.counterparty_phone;
  const phoneHref = telHref(phone);
  const email = row.counterparty_email;

  // @block 2.1 Key summary points — derived from row context.
  const points: string[] = [];
  if (row.asking_price != null || row.revenue != null) {
    const ask = row.asking_price ? `ask ${fmtUsd(row.asking_price)}` : null;
    const rev = row.revenue ? `rev ${fmtUsd(row.revenue)}` : null;
    const mult = (row.asking_price && row.revenue) ? `${(row.asking_price / row.revenue).toFixed(2)}x` : null;
    points.push([ask, rev, mult].filter(Boolean).join(' · '));
  }
  if (row.listing_state || row.deal_stage) {
    points.push([row.listing_state, row.deal_stage].filter(Boolean).join(' · '));
  }
  if (row.last_summary) {
    points.push(`Last touch (${fmtDate(row.last_touched_at)}): ${row.last_summary}`);
  }
  if (row.next_action) points.push(`Goal: ${row.next_action}`);

  const urg = row.urgency ?? 'undated';
  return (
    <li style={{ border: '1px solid var(--t2-border)', borderRadius: 6, padding: '12px 14px', display: 'grid', gap: 8 }}>
      {/* Header: idx · firm · rep · urgency · due */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--tx-3)', fontSize: 12, minWidth: 22 }}>{idx}.</span>
        <span style={{ fontWeight: 600, fontSize: 14 }}>{row.broker_company ?? '(no broker)'}</span>
        {row.counterparty_name && <span style={{ color: 'var(--tx-2)', fontSize: 13 }}>· {row.counterparty_name}</span>}
        <span style={{ flex: 1 }} />
        <span className={`conf conf-${urg === 'overdue' ? 'low' : urg === 'today' ? 'medium' : 'high'}`}>
          {URGENCY_LABEL[urg] ?? urg}
        </span>
        <span className="mono" style={{ color: 'var(--tx-3)', fontSize: 12 }}>{fmtDate(row.next_action_due)}</span>
      </div>

      {/* Contact line */}
      {(phone || email) && (
        <div style={{ display: 'flex', gap: 14, fontSize: 13, color: 'var(--tx-2)', flexWrap: 'wrap' }}>
          {phone && (
            <span>{phoneHref ? <a href={phoneHref} style={{ color: 'var(--brand-3)', textDecoration: 'none' }}>{phone}</a> : phone}</span>
          )}
          {email && <span><a href={`mailto:${email}`} style={{ color: 'var(--brand-3)', textDecoration: 'none' }}>{email}</a></span>}
        </div>
      )}

      {/* Listing line */}
      <div style={{ fontSize: 13, color: 'var(--tx-2)' }}>
        {row.deal_id ? (
          <NavLink to={`/acquisitions/${row.deal_id}`} style={{ color: 'var(--brand-3)', textDecoration: 'none', fontWeight: 500 }}>
            {row.source_listing_id ?? row.listing_name ?? 'View deal'}
          </NavLink>
        ) : (
          <span style={{ fontWeight: 500 }}>{row.source_listing_id ?? row.listing_name}</span>
        )}
        {row.source_listing_id && row.listing_name && <span style={{ color: 'var(--tx-3)' }}> — {row.listing_name}</span>}
      </div>

      {/* Key summary points */}
      {points.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--tx-2)', display: 'grid', gap: 3 }}>
          {points.map((p, i) => <li key={i}>{p}</li>)}
        </ul>
      )}
    </li>
  );
}
